<script>
    import {onDestroy} from 'svelte';
    import * as Utils from '@js/modules/utils';
    import I18n from '@js/modules/translations';

    import {mapStore} from '@js/stores/mapStore';

    import {applyRideType} from '@js/actions/mapActions';

    import ComboSearch from '@js/components/search/ComboSearch.svelte';

    import PWAManager from '@js/modules/pwaManager';
    import AnalyticsService from '@js/modules/analyticsService';
    import UsageTotals from '@js/modules/usageTotals';
    import Notification from '@js/components/layouts/notification';

    const {
        rideType,
        mapId
    } = $props();

    // Seed the map's activity from the prop without persisting it as the user's default (not a user choice).
    applyRideType(rideType, {persist: false});

    // The bar may be gone (or the geolocation answer late): a scroll still pending must not fire then.
    let _scrollTimer;
    let _destroyed = false;
    const _scrollToMap = (delay) => {
        if (_destroyed) {
            return;
        }

        clearTimeout(_scrollTimer);
        _scrollTimer = setTimeout(() => Utils.scrollTo(mapId), delay);
    };
    onDestroy(() => {
        _destroyed = true;
        clearTimeout(_scrollTimer);
    });

    const _handleLocateMe = () => {
        Notification.info(I18n.t('js.maps.geolocation.in_progress'));

        AnalyticsService.trackPlannerGeolocation('Planner page');

        if (mapStore.map) {
            AnalyticsService.trackGeolocationClick('Planner page');

            mapStore.map.onGeolocation(
                (userLocation) => {
                    setTimeout(() => {
                        Utils.scrollTo(mapId);

                        // Clear current path on map on new search and ask for deletion
                        _clearRoute();

                        mapStore.mapLocation = {
                            type: 'location',
                            bbox: undefined,
                            center: [userLocation.longitude, userLocation.latitude],
                            placeType: 'geolocation',
                            value: 'geolocation'
                        };

                        Notification.info(I18n.t('js.maps.helpers.position_as_start'));
                    }, 350);

                    setTimeout(() => Notification.hide(), 600);
                },
                () => {
                    _scrollToMap(350);
                });
        }
    };

    const _handleSelectOnMap = () => {
        AnalyticsService.trackPlannerShowMap('Planner page');

        if (mapStore.map) {
            setTimeout(() => {
                Utils.scrollTo(mapId);

                Notification.info(I18n.t('js.maps.helpers.click_on_map'));
            }, 350);
        }
    };

    // A new place or position clears the route drawn: counted like the toolbar's « clear »
    const _clearRoute = () => {
        if (mapStore.map?.hasActivityData?.()) {
            UsageTotals.add('map_clear');
        }

        mapStore.map?.clearMap();
    };

    const handleRideTypeChange = (type) => {
        // The search bar calls back on a re-click of the current activity too: that is not a pick.
        if (type !== mapStore.currentRideType) {
            AnalyticsService.trackRideTypeSelected(type, 'planner');
        }

        applyRideType(type);
    };

    const handleSubmit = (comboSearch, {track = true} = {}) => {
        if (comboSearch.location?.name) {
            if (track) {
                AnalyticsService.trackPlannerPlaceSelected();
                AnalyticsService.trackSearchSubmitted('planner', comboSearch.location.placeType);
                AnalyticsService.trackSearch('Planner page', undefined, `${comboSearch.location.value} (${comboSearch.location.placeType})`, 1);
            }

            // Clear current path on map on new search and ask for deletion
            _clearRoute();

            mapStore.mapLocation = comboSearch.location;

            _scrollToMap(100);
        }

        if (comboSearch.useMyPosition) {
            _handleLocateMe();
        } else if (comboSearch.selectOnMap) {
            _handleSelectOnMap();
        } else {
            Notification.info(I18n.t('js.maps.helpers.draw_path'));
        }
    };
</script>

<div class="mb-6">
    <ComboSearch id="planner-search-input"
                 formId="planner-search"
                 placeholder={I18n.t('js.views.planner.search')}
                 searchFeatures={['location']}
                 defaultRideType={$mapStore.currentRideType}
                 rideTypeHiddenOnMobile={true}
                 autoFocus={PWAManager.getPWADisplayMode() === PWAManager.MODE.BROWSER}
                 useRecentSearch={true}
                 onRideTypeChange={handleRideTypeChange}
                 onSubmit={handleSubmit}/>
</div>
